import { useMemo, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Bot, Brain, CheckCircle2, Cpu, Info, Sparkles, Zap } from "lucide-react";
import { TextGenerationViewModel } from "@/app/core/chatPage/chatCopmonents/shared/TextGenerationViewModel";;
import { AIModel } from "@/app/core/chatPage/chatCopmonents/shared/constants";;
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

export const ModelSelectionPage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const typeFromUrl = searchParams.get('type') as 'text' | 'image' | 'video' | null;
  const [expandedModelId, setExpandedModelId] = useState<string | null>(null);
  const viewModel = TextGenerationViewModel.getInstance();

  useEffect(() => {
    if (typeFromUrl) {
      viewModel.setActiveModelType(typeFromUrl);
    }
  }, [typeFromUrl, viewModel]);

  const modelsToShow = viewModel.availableModelsByType;

  const modelsByProvider = useMemo(() => {
    return modelsToShow.reduce<Record<string, AIModel[]>>((acc, model) => {
      const provider = model.provider || "other";
      if (!acc[provider]) acc[provider] = [];
      acc[provider].push(model);
      return acc;
    }, {});
  }, [modelsToShow]);
  const getModelVisualMeta = (model: AIModel) => {
    const normalized = `${model.id} ${model.name} ${model.provider || ""}`.toLowerCase();
    if (normalized.includes("claude") || normalized.includes("anthropic")) {
      return { icon: Brain, tierClass: "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white" };
    }
    if (normalized.includes("gemini") || normalized.includes("google")) {
      return { icon: Cpu, tierClass: "bg-blue-500/10 text-blue-500" };
    }
    if (normalized.includes("gpt") || normalized.includes("openai")) {
      return { icon: Sparkles, tierClass: "bg-blue-500/10 text-blue-500" };
    }
    return { icon: Bot, tierClass: "bg-blue-500/10 text-blue-500" };
  };

  const getModelDetails = (model: AIModel) => {
    const text = `${model.name} ${model.description} ${model.provider || ""}`.toLowerCase();
    const strengths = [t('fastStart'), t('suitableForDaily')];
    const weaknesses = [t('qualityDependsOnPrompt')];
    const useCases = [t('dialogue'), t('textGeneration')];

    if (text.includes("gpt")) {
      strengths.unshift(t('strongComplexInstructions'));
      useCases.push(t('codeAndAnalysis'));
    } else if (text.includes("claude")) {
      strengths.unshift(t('holdsLongContext'));
      useCases.push(t('largeDocuments'));
    } else if (text.includes("gemini")) {
      strengths.unshift(t('universalBalance'));
      useCases.push(t('fastAnswers'));
    }

    return {
      strengths: strengths.slice(0, 3),
      weaknesses: weaknesses.slice(0, 2),
      useCases: useCases.slice(0, 3),
    };
  };

  return (
    <div className="min-h-full bg-background">
      <div className="mx-auto flex h-full w-full max-w-4xl flex-1 flex-col md:px-6">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border/10 bg-background/95 px-4 py-4 backdrop-blur-xl md:pt-8">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="rounded-full bg-muted/50 p-2 transition-colors hover:bg-muted" aria-label={t('back')}>
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold">{t('selectModelTitle')}</h3>
          </div>
        </div>

        <div className="scrollbar-hide flex-1 space-y-8 overflow-y-auto px-4 py-6">
          {Object.entries(modelsByProvider).map(([provider, models]) => (
            <div key={provider}>
              <div className="mb-4">
                <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{provider}</h4>
              </div>
              <div className="space-y-3">
                {models.map((model) => {
                  const isSelected = viewModel.selectedModel.id === model.id;
                  const isExpanded = expandedModelId === model.id;
                  const details = getModelDetails(model);
                  const { icon: ModelIcon, tierClass } = getModelVisualMeta(model);
                  const description = viewModel.getTranslatedModelDescription(model.id, t as (key: string) => string);

                  return (
                    <div
                      key={model.id}
                      className={`relative overflow-hidden rounded-2xl border transition-all duration-200 ${
                        isSelected ? "border-accent bg-accent/5 shadow-md shadow-accent/10" : "border-border bg-card hover:border-accent/30 hover:shadow-sm"
                      }`}
                    >
                      <div
                        className="flex cursor-pointer items-center gap-4 p-4"
                        onClick={() => {
                          viewModel.setSelectedModel(model);
                          navigate(-1);
                        }}
                      >
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm ${
                            isSelected ? "bg-accent text-accent-foreground" : "border border-border bg-background text-foreground"
                          }`}
                        >
                          <ModelIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-0.5 flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">{model.name}</span>
                            {isSelected ? <CheckCircle2 className="h-4 w-4 shrink-0 text-accent" /> : null}
                            <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tierClass}`}>pro</span>
                          </div>
                          <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
                          <p className="mt-1 text-[10px] text-muted-foreground/60">{t('contextStandard')}</p>
                        </div>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            setExpandedModelId(isExpanded ? null : model.id);
                          }}
                          className="shrink-0 rounded-lg p-2 transition-colors hover:bg-muted/50"
                          aria-label={t('showDetails')}
                        >
                          <Info className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? "rotate-180 text-accent" : "text-muted-foreground"}`} />
                        </button>
                      </div>

                      <div className={`grid transition-all duration-300 ${isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                        <div className="overflow-hidden">
                          <div className="border-t border-border/10 px-4 pb-4 pt-2">
                            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                              <div className="rounded-xl bg-emerald-500/5 p-3">
                                <h5 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                  <Zap className="h-3 w-3" /> {t('strengths')}
                                </h5>
                                <ul className="space-y-1">
                                  {details.strengths.map((s) => (
                                    <li key={s} className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground/80">
                                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-500/50" /> {s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="rounded-xl bg-rose-500/5 p-3">
                                <h5 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-rose-600 dark:text-rose-400">
                                  <AlertTriangle className="h-3 w-3" /> {t('weaknesses')}
                                </h5>
                                <ul className="space-y-1">
                                  {details.weaknesses.map((w) => (
                                    <li key={w} className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground/80">
                                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-rose-500/50" /> {w}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            <div className="mt-3">
                              <h5 className="mb-2 text-[11px] font-semibold text-foreground/70">{t('idealFor')}</h5>
                              <div className="flex flex-wrap gap-1.5">
                                {details.useCases.map((useCase) => (
                                  <span key={useCase} className="rounded-md border border-border/50 bg-muted/50 px-2 py-1 text-[10px] font-medium text-foreground/80">
                                    {useCase}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
});